import { withNamespaces } from "react-i18next";
import { Alert, Button } from "reactstrap";

import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";

const dateUtils = new DateUtils();

const formatApprovalDate = (value) => {
    if (value == null || value === "") {
        return null;
    }

    if (
        typeof value === "number" ||
        (typeof value === "string" && /^\d+$/.test(value))
    ) {
        return (
            dateUtils.convertTimeStampToDate(
                Number(value),
                INTERNATIONAL_DATE_FORMAT
            ) || null
        );
    }

    return (
        dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT) || null
    );
};

const MetaBlock = ({ t, labelKey, value, valueClassName }) => {
    if (!value) {
        return null;
    }

    return (
        <div className="required-approvals-card__meta">
            <div className="required-approvals-card__meta-label">
                {t(labelKey)}
            </div>
            <div className={valueClassName || "required-approvals-card__meta-value"}>
                {value}
            </div>
        </div>
    );
};

const BADGE_TONES = {
    success: "success",
    warning: "warning",
    danger: "rose",
    secondary: "neutral"
};

const StatusBadge = ({ t, status }) => {
    if (!status?.labelKey) {
        return null;
    }

    const tone = BADGE_TONES[status.color] || "neutral";

    return (
        <span className={`required-approvals-badge ${tone}`}>
            {status.showDot ? (
                <i
                    className="ri-checkbox-blank-circle-fill"
                    aria-hidden="true"
                />
            ) : null}
            {t(status.labelKey)}
        </span>
    );
};

const ApprovalCard = ({
    t,
    title,
    viewModel,
    isSubmitting,
    onAssign,
    onApprove,
    onReject,
    onChangeDecision,
    onGoToAssessment,
}) => {
    const status = viewModel?.statusPresentation || {};
    const assignmentDate = formatApprovalDate(viewModel?.lastUpdated);
    const approvalDate = formatApprovalDate(viewModel?.approvalDate);
    const rejectionDate = formatApprovalDate(viewModel?.rejectionDate);
    const assignedByName = viewModel?.assignedByName
        ? viewModel.assignedByName
        : viewModel?.assignedByIsNull
          ? t("System")
          : null;
    const hasFooterActions =
        viewModel?.showAssign ||
        viewModel?.showChangeAssignee ||
        viewModel?.showApproveReject ||
        viewModel?.showChangeDecision;

    return (
        <div className="required-approvals-card">
            <div className="required-approvals-card__header">
                <div className="required-approvals-card__title">
                    {t(title)}
                </div>
                <div className="required-approvals-card__status-row">
                    <StatusBadge t={t} status={status} />
                    {viewModel?.showGoToAssessment ? (
                        <Button
                            color="link"
                            className="required-approvals-card__link"
                            type="button"
                            onClick={onGoToAssessment}
                        >
                            {t("Go to Assessment")}
                            <i
                                className="ri-arrow-right-up-line ms-1"
                                aria-hidden="true"
                            />
                        </Button>
                    ) : null}
                </div>
            </div>

            <div className="required-approvals-card__assigned">
                <div className="required-approvals-card__meta mb-0">
                    <div className="required-approvals-card__meta-label">
                        {t("Assigned To")}
                    </div>
                    <div className="required-approvals-card__meta-value">
                        {viewModel?.assignedToName || t("Not assigned")}
                    </div>
                    {viewModel?.assignedToRole ? (
                        <div className="required-approvals-card__meta-role">
                            {t(viewModel.assignedToRole)}
                        </div>
                    ) : null}
                </div>
            </div>

            <MetaBlock t={t} labelKey="Assigned by" value={assignedByName} />
            <MetaBlock t={t} labelKey="Assignment date" value={assignmentDate} />
            <MetaBlock
                t={t}
                labelKey="Approval date"
                value={approvalDate}
                valueClassName="required-approvals-card__meta-value is-success"
            />
            <MetaBlock
                t={t}
                labelKey="Rejection date"
                value={rejectionDate}
                valueClassName="required-approvals-card__meta-value is-danger"
            />

            {viewModel?.rejectionReason ? (
                <Alert color="danger" className="required-approvals-card__reason">
                    <div className="required-approvals-card__meta-label mb-1">
                        {t("Reason")}
                    </div>
                    <div>{viewModel.rejectionReason}</div>
                </Alert>
            ) : null}

            {hasFooterActions ? (
                <div className="required-approvals-card__actions">
                    {viewModel?.showAssign ? (
                        <Button
                            color="primary"
                            outline
                            type="button"
                            onClick={onAssign}
                            disabled={isSubmitting}
                        >
                            {t("Assign")}
                        </Button>
                    ) : null}
                    {viewModel?.showChangeAssignee ? (
                        <Button
                            color="link"
                            className="required-approvals-card__link"
                            type="button"
                            onClick={onAssign}
                            disabled={isSubmitting}
                        >
                            {t("Change assignee")}
                        </Button>
                    ) : null}
                    {viewModel?.showApproveReject ? (
                        <>
                            <Button
                                color="success"
                                type="button"
                                onClick={onApprove}
                                disabled={isSubmitting}
                            >
                                {t("Approve")}
                            </Button>
                            <Button
                                color="danger"
                                outline
                                type="button"
                                onClick={onReject}
                                disabled={isSubmitting}
                            >
                                {t("Reject")}
                            </Button>
                        </>
                    ) : null}
                    {viewModel?.showChangeDecision ? (
                        <Button
                            color="link"
                            className="required-approvals-card__link"
                            type="button"
                            onClick={onChangeDecision}
                            disabled={isSubmitting}
                        >
                            {t("Change decision")}
                        </Button>
                    ) : null}
                </div>
            ) : (
                <div className="required-approvals-card__actions-spacer" />
            )}
        </div>
    );
};

export default withNamespaces()(ApprovalCard);
